import React, { useCallback, useEffect, useRef, useState } from "react";
import ChatMessage from "./components/ChatMessage";
// import AttachIcon from "./components/AttachIcon";
import SendIcon from "./components/SendIcon";
import { useClientChat } from "./hooks/useClientChat";
import chatBackgroundUrl from "../assets/chatbackground.png";
import styles from "./ClientChatPage.module.css";

export interface ClientChatPageProps {
  /** UUID клиента из маршрута /chat/:uuid */
  uuid: string;
}

const ClientChatPage: React.FC<ClientChatPageProps> = ({ uuid }) => {
  const {
    messages,
    messageText,
    setMessageText,
    isReady,
    isSending,
    isConnecting,
    error,
    sendMessage,
  } = useClientChat(uuid);

  const pageRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const initialScrollTimeoutRef = useRef<number | null>(null);
  const layoutSyncTimersRef = useRef<number[]>([]);
  const [isInitialHistoryLoading, setIsInitialHistoryLoading] = useState(true);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    setIsInitialHistoryLoading(true);
    if (initialScrollTimeoutRef.current !== null) {
      window.clearTimeout(initialScrollTimeoutRef.current);
      initialScrollTimeoutRef.current = null;
    }
  }, [uuid]);

  const scrollMessagesToBottom = () => {
    const scroller = messagesScrollRef.current;
    if (!scroller) return;
    scroller.scrollTop = scroller.scrollHeight;
  };

  /**
   * iOS WKWebView usually overlays the keyboard without shrinking layout height.
   * - keyboard open  → pin shell to visualViewport (visible area above keyboard)
   * - keyboard closed → stretch to all edges (top/bottom/left/right = 0)
   */
  const syncPageLayout = useCallback(() => {
    const page = pageRef.current;
    if (!page) return;

    const vv = window.visualViewport;
    const keyboardOpen = Boolean(vv && window.innerHeight - vv.height > 80);

    page.style.position = "fixed";
    page.style.transform = "none";

    if (keyboardOpen && vv) {
      page.style.top = `${Math.round(vv.offsetTop)}px`;
      page.style.left = `${Math.round(vv.offsetLeft)}px`;
      page.style.width = `${Math.round(vv.width)}px`;
      page.style.height = `${Math.max(0, Math.round(vv.height))}px`;
      page.style.right = "auto";
      page.style.bottom = "auto";
      setIsKeyboardOpen(true);
      return;
    }

    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Stretch to the real screen edges — avoids a white body strip under a short height.
    page.style.top = "0px";
    page.style.left = "0px";
    page.style.right = "0px";
    page.style.bottom = "0px";
    page.style.width = "auto";
    page.style.height = "auto";
    setIsKeyboardOpen(false);
  }, []);

  const scheduleLayoutSync = useCallback(() => {
    syncPageLayout();
    for (const timerId of layoutSyncTimersRef.current) {
      window.clearTimeout(timerId);
    }
    layoutSyncTimersRef.current = [50, 250, 400].map((delay) =>
      window.setTimeout(() => {
        syncPageLayout();
        scrollMessagesToBottom();
      }, delay)
    );
  }, [syncPageLayout]);

  useEffect(() => {
    if (messages.length === 0) return;

    if (isInitialHistoryLoading) {
      scrollMessagesToBottom();

      if (initialScrollTimeoutRef.current !== null) {
        window.clearTimeout(initialScrollTimeoutRef.current);
      }

      initialScrollTimeoutRef.current = window.setTimeout(() => {
        scrollMessagesToBottom();
        setIsInitialHistoryLoading(false);
        initialScrollTimeoutRef.current = null;
      }, 120);
      return;
    }

    scrollMessagesToBottom();
  }, [isInitialHistoryLoading, messages.length]);

  useEffect(
    () => () => {
      if (initialScrollTimeoutRef.current !== null) {
        window.clearTimeout(initialScrollTimeoutRef.current);
      }
      for (const timerId of layoutSyncTimersRef.current) {
        window.clearTimeout(timerId);
      }
    },
    []
  );

  useEffect(() => {
    const textarea = messageInputRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";

    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(computedStyle.lineHeight || "20") || 20;
    const paddingTop = Number.parseFloat(computedStyle.paddingTop || "0") || 0;
    const paddingBottom = Number.parseFloat(computedStyle.paddingBottom || "0") || 0;
    const maxHeight = Math.round(lineHeight * 3 + paddingTop + paddingBottom);

    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [messageText]);

  useEffect(() => {
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousViewportContent = viewportMeta?.getAttribute("content") ?? null;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyBackground = document.body.style.backgroundColor;
    const previousHtmlBackground = document.documentElement.style.backgroundColor;
    const root = document.getElementById("root");
    const previousRootBackground = root?.style.backgroundColor ?? "";

    if (viewportMeta) {
      viewportMeta.setAttribute(
        "content",
        "width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover"
      );
    }

    // Match sendForm panel color so any edge gap on iOS is not white.
    const chatChromeColor = "#021d4d";
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.documentElement.style.backgroundColor = chatChromeColor;
    document.body.style.backgroundColor = chatChromeColor;
    if (root) {
      root.style.backgroundColor = chatChromeColor;
    }

    syncPageLayout();

    const onViewportChange = () => {
      syncPageLayout();
    };

    const onResize = () => {
      scheduleLayoutSync();
    };

    window.visualViewport?.addEventListener("resize", onViewportChange);
    window.visualViewport?.addEventListener("scroll", onViewportChange);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    let lastTouchEnd = 0;
    const preventGesture = (event: Event) => {
      event.preventDefault();
    };
    const preventPinchZoom = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };
    const preventDoubleTapZoom = (event: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    document.addEventListener("gesturestart", preventGesture, { passive: false });
    document.addEventListener("gesturechange", preventGesture, { passive: false });
    document.addEventListener("gestureend", preventGesture, { passive: false });
    document.addEventListener("touchstart", preventPinchZoom, { passive: false });
    document.addEventListener("touchend", preventDoubleTapZoom, { passive: false });

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.backgroundColor = previousBodyBackground;
      document.documentElement.style.backgroundColor = previousHtmlBackground;
      if (root) {
        root.style.backgroundColor = previousRootBackground;
      }

      if (viewportMeta) {
        if (previousViewportContent !== null) {
          viewportMeta.setAttribute("content", previousViewportContent);
        } else {
          viewportMeta.removeAttribute("content");
        }
      }

      window.visualViewport?.removeEventListener("resize", onViewportChange);
      window.visualViewport?.removeEventListener("scroll", onViewportChange);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      document.removeEventListener("gesturestart", preventGesture);
      document.removeEventListener("gesturechange", preventGesture);
      document.removeEventListener("gestureend", preventGesture);
      document.removeEventListener("touchstart", preventPinchZoom);
      document.removeEventListener("touchend", preventDoubleTapZoom);
    };
  }, [scheduleLayoutSync, syncPageLayout]);

  const onInputFocus = () => {
    scheduleLayoutSync();
    window.requestAnimationFrame(() => scrollMessagesToBottom());
  };

  const onInputBlur = () => {
    scheduleLayoutSync();
  };

  const canSend = isReady && !isSending && messageText.trim().length > 0;
  const showErrorOverlay = Boolean(error);
  const showCenteredOverlay =
    showErrorOverlay || (isConnecting && messages.length === 0);
  const showEmptyState = messages.length === 0 && !isConnecting && !error;
  const shouldRenderMessages = !showErrorOverlay;
  const showInitialLoader = messages.length > 0 && isInitialHistoryLoading;

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isTouchLikeDevice =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;

    if (isTouchLikeDevice) return;

    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSend) sendMessage();
    }
  };

  const onSendClick = () => {
    if (!canSend) return;
    sendMessage();
    messageInputRef.current?.focus();
  };

  return (
    <div
      className={`${styles.page}${isKeyboardOpen ? ` ${styles.pageKeyboardOpen}` : ""}`}
      ref={pageRef}
    >
      <main className={styles.main}>
        <div
          className={styles.messagesArea}
          style={{ backgroundImage: `url(${chatBackgroundUrl})` }}
        >
          <div className={styles.messagesScroll} ref={messagesScrollRef}>
            <div className={styles.messagesContent}>
              {shouldRenderMessages
                ? messages.map((message) => (
                    <ChatMessage
                      key={message.id}
                      text={message.text}
                      sender={message.sender}
                      timeLabel={message.timeLabel}
                      isOutgoing={message.isOutgoing}
                    />
                  ))
                : null}
              <div
                ref={messagesEndRef}
                className={styles.messagesBottomAnchor}
                aria-hidden="true"
              />
            </div>
          </div>

          {showCenteredOverlay ? (
            <div className={styles.centerOverlay}>
              {error ? (
                <p className={styles.serviceNotice}>{error}</p>
              ) : isConnecting ? (
                <p className={styles.state}>Подключение…</p>
              ) : null}
            </div>
          ) : null}

          {showEmptyState ? (
            <div className={styles.centerOverlay}>
              <p className={styles.state}>Сообщений пока нет</p>
            </div>
          ) : null}

          {showInitialLoader ? (
            <div className={styles.centerOverlay}>
              <div className={styles.loadingBox}>
                <span className={styles.loadingSpinner} aria-hidden="true" />
                <span className={styles.loadingText}>Загрузка сообщений…</span>
              </div>
            </div>
          ) : null}
        </div>

        <div className={styles.sendForm}>
          <div className={styles.sendPrimaryRow}>
            <textarea
              ref={messageInputRef}
              className={styles.sendInput}
              value={messageText}
              onChange={(event) => setMessageText(event.target.value)}
              onKeyDown={onInputKeyDown}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              placeholder="Текст сообщения"
              disabled={!isReady || isSending}
              rows={1}
              enterKeyHint="enter"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="sentences"
              spellCheck={false}
            />
            <div
              className={`${styles.sendButton}${canSend ? "" : ` ${styles.sendButtonDisabled}`}`}
              role="button"
              aria-label="Отправить"
              aria-disabled={!canSend}
              onTouchEnd={(event) => {
                event.preventDefault();
                onSendClick();
              }}
              onClick={onSendClick}
            >
              <SendIcon />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientChatPage;
